import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import debounce from 'lodash/debounce'
import Icon from '../shared/icon'
import { useCatalogue } from './CatalogueProvider'
import { searchProducts } from './catalogueApi'
import { CatalogueItemImage } from './category_table/CatalogueTableCells'
import { ProductRow } from './catalogueTypes'
import { t } from '../../i18n'

const MIN_QUERY_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 300

const Breadcrumbs = () => {
  const { state, meta } = useCatalogue()
  const { selectedCategory } = state

  if (!selectedCategory) return <h3 className="fw-bold margin-left-20">{t('catalogue.all_categories')}</h3>

  return (
    <ol className="breadcrumb">
      <li className="breadcrumb-item">
        <Link to={meta.paths.category(null)}>{t('catalogue.all_categories')}</Link>
      </li>
      {selectedCategory.ancestry.map((category) => (
        <li key={category.id} className="breadcrumb-item" aria-current="page">
          <Link to={meta.paths.category(category.id)} className={category.id === selectedCategory.id ? 'active' : ''}>
            {category.name}
          </Link>
        </li>
      ))}
    </ol>
  )
}

const HeaderActions = () => {
  const { state, actions, meta } = useCatalogue()

  if (state.selectedCategory?.batch_recipes) return null

  const showEditToggle = state.panel.kind === 'browse'

  if (state.editMode) {
    return (
      <div className="category-buttons">
        <button type="button" className="btn btn-secondary ms-3 done-button" onClick={actions.toggleEditMode}>
          <Icon name="doneIcon" /> <span className="secondary-edit-icon-text">{t('catalogue.done')}</span>
        </button>
      </div>
    )
  }

  return (
    <div className="category-buttons">
      {showEditToggle ? (
        <button type="button" className="btn btn-secondary ms-3 d-flex align-items-center" onClick={actions.toggleEditMode}>
          <div className="center">
            <Icon name="editIcon" className="secondary-edit-icon me-1" />
            <span className="secondary-edit-icon-text">{t('global.edit')}</span>
          </div>
        </button>
      ) : null}
      {state.panel.kind === 'new' ? null : (
        <button type="button" className="btn btn-primary ms-3" onClick={() => meta.go.newItem('product')}>
          {t('catalogue.add_new')}
        </button>
      )}
    </div>
  )
}

interface SearchResults {
  query: string
  products: ProductRow[]
}

type DebouncedSearch = ((value: string, categoryId: number | null) => void) & { cancel(): void }

const groupByCategory = (products: ProductRow[]): ProductRow[][] => {
  const groups = new Map<number, ProductRow[]>()
  products.forEach((product) => {
    const group = groups.get(product.product_category_id)
    if (group) {
      group.push(product)
    } else {
      groups.set(product.product_category_id, [product])
    }
  })
  return Array.from(groups.values())
}

const ProductSearch = () => {
  const { state, meta } = useCatalogue()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults>({ query: '', products: [] })
  const [open, setOpen] = useState(false)
  const inFlight = useRef<AbortController | null>(null)

  const search: DebouncedSearch = useMemo(
    () =>
      debounce((value: string, categoryId: number | null) => {
        inFlight.current?.abort()
        const controller = new AbortController()
        inFlight.current = controller
        searchProducts(value, categoryId, controller.signal)
          .then((products) => setResults({ query: value, products }))
          .catch(() => undefined)
      }, SEARCH_DEBOUNCE_MS),
    []
  )

  useEffect(
    () => () => {
      search.cancel()
      inFlight.current?.abort()
    },
    [search]
  )

  const listVisible = open && query.length >= MIN_QUERY_LENGTH && results.query === query

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)
    setOpen(true)
    if (value.length >= MIN_QUERY_LENGTH) {
      search(value, state.categoryId)
    } else {
      search.cancel()
    }
  }

  const pick = (product: ProductRow) => {
    setOpen(false)
    meta.go.show(product, product.product_category_id)
  }

  return (
    <div id="autocomplete" className="autocomplete-container">
      <input
        className="input-without-style"
        type="search"
        autoComplete="off"
        placeholder={t('search')}
        value={query}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />
      {listVisible ? (
        <div className="autocomplete-list" onMouseDown={(event) => event.preventDefault()}>
          {groupByCategory(results.products).map((group) => (
            <div className="autocomplete-list-item" key={group[0].product_category_id}>
              <Link to={meta.paths.category(group[0].product_category_id)} onClick={() => setOpen(false)}>
                {group[0].product_category_name}
              </Link>
              {group.map((product) => (
                <p className="list-item" key={product.id} onClick={() => pick(product)}>
                  <CatalogueItemImage item={product} placeholder="categoryPlaceholder" className="table-row-image" />
                  {product.name}
                </p>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const CatalogueHeader = () => (
  <div className="header mb-3 d-flex justify-content-between align-items-center">
    <Breadcrumbs />
    <div className="d-flex align-items-center">
      <div className="d-flex align-items-center form-control">
        <i className="bi bi-search"></i>
        <ProductSearch />
      </div>
      <HeaderActions />
    </div>
  </div>
)

export default CatalogueHeader
